import { useMemo, useRef, useState } from 'react';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import SendIcon from '@mui/icons-material/Send';
import {
    Box,
    Button,
    CircularProgress,
    ClickAwayListener,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    Typography
} from '@mui/material';
import { BentoThemeProvider } from '@zenput/ui-bento';
import { MentionHighlightedInput } from './MentionHighlightedInput';
import { getMentionQuery, PostMention, TrackedMention } from './mentionText.utils';
import { useMentionSearch } from './useMentionSearch';
import { MentionableUser, useMentionState } from './useMentionState';

const isPlural = (count: number) => count === 0 || count > 1;

interface FollowUpActionCommentsInputProps {
    onSubmit: (message: string, mentions: PostMention[]) => void;
    disabled: boolean;
    currentUserId?: number;
    locationId?: number;
    savedNotifierIds: Map<number, TrackedMention>;
    checkMentionPermission: (userId: number) => Promise<boolean>;
}

export const FollowUpActionCommentsInput = (props: FollowUpActionCommentsInputProps) => {
    const { onSubmit, disabled, currentUserId, locationId, savedNotifierIds, checkMentionPermission } = props;

    const [message, setMessage] = useState('');
    const [cursorPos, setCursorPos] = useState(0);
    const [dismissed, setDismissed] = useState(false);
    const [notifyDialogOpen, setNotifyDialogOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const { trackedMentions, addMention, removeStaleMentions, buildSubmitPayload, reset } = useMentionState();

    const mentionQuery = useMemo(() => getMentionQuery(message, cursorPos), [message, cursorPos]);
    const prevMentionQueryRef = useRef(mentionQuery);
    if (
        prevMentionQueryRef.current?.query !== mentionQuery?.query ||
        prevMentionQueryRef.current?.atIndex !== mentionQuery?.atIndex
    ) {
        prevMentionQueryRef.current = mentionQuery;
        setDismissed(false);
    }

    const isAutocompleteOpen = mentionQuery !== null && !dismissed;
    const { users, isLoading, debouncedQuery } = useMentionSearch(
        mentionQuery?.query ?? '',
        isAutocompleteOpen,
        locationId
    );

    const notifiers = useMemo(() => {
        const combined = new Map(savedNotifierIds);
        trackedMentions.forEach((mention) => {
            if (mention.id !== currentUserId) {
                combined.set(mention.id, mention);
            }
        });
        return combined;
    }, [savedNotifierIds, trackedMentions, currentUserId]);
    const notifierList = Array.from(notifiers.values());
    const subscribedNotifiers = notifierList.filter((mention) => mention.isSubscriber);
    const unsubscribedNotifiers = notifierList.filter((mention) => !mention.isSubscriber);

    const notifierCountText = _t(
        isPlural(subscribedNotifiers.length)
            ? 'mobile_comments_mention_description_notifiers_plural'
            : 'mobile_comments_mention_description_notifiers',
        { count: subscribedNotifiers.length }
    );
    const mentionInstructionsText = _t('mobile_comments_mention_instructions');

    const handleClickAway = () => {
        if (isAutocompleteOpen) {
            setDismissed(true);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setMessage(newValue);
        setCursorPos(e.target.selectionStart ?? newValue.length);
        removeStaleMentions(newValue);
    };

    const handleInputSelect = (e: React.SyntheticEvent) => {
        const target = e.target as HTMLInputElement;
        setCursorPos(target.selectionStart ?? 0);
    };

    const handleAtButtonClick = () => {
        const input = inputRef.current;
        if (!input) {
            return;
        }

        const cursor = input.selectionStart ?? message.length;
        const before = message.slice(0, cursor);
        const after = message.slice(cursor);
        const needsSpace = before.length > 0 && !/\s$/.test(before);
        const insert = needsSpace ? ' @' : '@';
        const newMessage = `${before}${insert}${after}`;
        const newCursor = cursor + insert.length;

        setMessage(newMessage);
        setCursorPos(newCursor);

        requestAnimationFrame(() => {
            input.focus();
            input.setSelectionRange(newCursor, newCursor);
        });
    };

    const handleSelectUser = async (selectedUser: MentionableUser) => {
        const isSubscriberPromise = checkMentionPermission(selectedUser.id);
        setDismissed(true);
        const currentQuery = mentionQuery;
        const currentCursorPos = cursorPos;

        const input = inputRef.current;
        if (!input || !currentQuery) {
            return;
        }

        const mentionText = `@${selectedUser.displayName} `;
        const before = message.slice(0, currentQuery.atIndex);
        const after = message.slice(currentCursorPos);
        const newMessage = `${before}${mentionText}${after}`;
        const newCursor = before.length + mentionText.length;

        setMessage(newMessage);
        setCursorPos(newCursor);

        requestAnimationFrame(() => {
            input.focus();
            input.setSelectionRange(newCursor, newCursor);
        });

        const isSubscriber = await isSubscriberPromise;
        addMention(selectedUser, isSubscriber);
    };

    const handleSend = () => {
        const trimmed = message.trim();
        if (!trimmed || disabled) {
            return;
        }

        const { message: tokenizedMessage, mentions } = buildSubmitPayload(trimmed);
        onSubmit(tokenizedMessage, mentions);
        setMessage('');
        setCursorPos(0);
        reset();
    };

    const handleNotifierLineClick = (e: React.MouseEvent<HTMLElement>) => {
        const target = e.target as HTMLElement;
        if (target.closest('.show_users_to_notify')) {
            e.preventDefault();
            setNotifyDialogOpen(true);
            return;
        }
        if (target.closest('.open_mention_autocomplete')) {
            e.preventDefault();
            handleAtButtonClick();
        }
    };

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <Box sx={{ width: '100%', paddingX: 2, paddingY: 1.5, position: 'relative', backgroundColor: 'white' }}>
                {isAutocompleteOpen && (
                    <Paper
                        variant="outlined"
                        elevation={0}
                        sx={{
                            position: 'absolute',
                            bottom: '100%',
                            left: 2,
                            right: 2,
                            maxHeight: 240,
                            overflowY: 'auto'
                        }}
                    >
                        <List disablePadding>
                            {isLoading && (
                                <ListItemButton disabled>
                                    <CircularProgress size={20} sx={{ marginRight: 1 }} />
                                </ListItemButton>
                            )}
                            {!isLoading && debouncedQuery.length > 0 && users.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ padding: 2 }}>
                                    {_t('mobile_comments_no_matching_users')}
                                </Typography>
                            )}
                            {!isLoading &&
                                users.map((user) => (
                                    <ListItemButton key={user.id} onClick={() => handleSelectUser(user)}>
                                        <ListItemText
                                            primary={user.displayName}
                                            secondary={user.userRole || undefined}
                                        />
                                    </ListItemButton>
                                ))}
                        </List>
                    </Paper>
                )}

                <Typography
                    variant="caption"
                    color="text.secondary"
                    component="div"
                    onClick={handleNotifierLineClick}
                    dangerouslySetInnerHTML={{ __html: `${notifierCountText} ${mentionInstructionsText}` }}
                    sx={{
                        marginBottom: 0.5,
                        '& a': {
                            color: 'primary.main',
                            fontWeight: 500,
                            cursor: 'pointer'
                        }
                    }}
                />
                <Divider sx={{ marginX: -2, marginBottom: 1.5 }} />
                <Stack direction="row" alignItems="flex-end" gap={1}>
                    <IconButton onClick={handleAtButtonClick} color="primary" size="small" disabled={disabled}>
                        <AlternateEmailIcon fontSize="small" />
                    </IconButton>
                    <MentionHighlightedInput
                        inputRef={inputRef}
                        trackedMentions={trackedMentions}
                        value={message}
                        onChange={handleChange}
                        onSelect={handleInputSelect}
                        disabled={disabled}
                    />
                    <IconButton color="primary" disabled={disabled || !message.trim()} onClick={handleSend}>
                        <SendIcon />
                    </IconButton>
                </Stack>

                <BentoThemeProvider>
                    <Dialog fullWidth open={notifyDialogOpen} onClose={() => setNotifyDialogOpen(false)}>
                        <DialogTitle>{_t('mobile_comments_users_to_notify_title')}</DialogTitle>
                        <DialogContent>
                            {subscribedNotifiers.length === 0 && unsubscribedNotifiers.length === 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    {_t('mobile_comments_empty_users_to_notify')}
                                </Typography>
                            )}
                            {subscribedNotifiers.length > 0 && (
                                <List disablePadding>
                                    {subscribedNotifiers.map((mention) => (
                                        <ListItem key={mention.id} disableGutters disablePadding>
                                            <ListItemText primary={mention.displayName} secondary={mention.userRole} />
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                            {unsubscribedNotifiers.length > 0 && (
                                <Box marginTop={2}>
                                    <Typography variant="subtitle2" textAlign="center" color="text.disabled">
                                        {_t('mobile_comments_users_cant_view_message')}
                                    </Typography>
                                    <List disablePadding>
                                        {unsubscribedNotifiers.map((mention) => (
                                            <ListItem key={mention.id} disableGutters disablePadding>
                                                <ListItemText
                                                    primary={mention.displayName}
                                                    secondary={mention.userRole}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button variant="text" onClick={() => setNotifyDialogOpen(false)}>
                                {_t('mobile_tasks_legend_modal_footer_button_title')}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </BentoThemeProvider>
            </Box>
        </ClickAwayListener>
    );
};
